import { Component, Input, output } from '@angular/core';

export type ChubbChartBarData = {
  color: string;
  value: number;
  label: string;
  displayValue?: number;
};

type ChubbChartBarPath = {
  color: string;
  d: string;
};

export type ChubbChartYAxisLabel = {
  label: string;
  y: number;
};

@Component({
  selector: 'chubb-chart-bar',
  standalone: true,
  imports: [ ],
  templateUrl: './chubb-chart-bar.component.html'
})
export class ChubbChartBarComponent {
  @Input() width: number = 352;
  @Input() height: number = 211;
  @Input() paddingLeft: number = 50;
  @Input() paddingRight: number = 20;
  @Input() paddingTop: number = 20;
  @Input() paddingBottom: number = 40;
  @Input() barRadiusTop: number = 5;
  @Input() barWidth: number = 0;
  @Input() yDivision: number = 10;
  @Input() yLabelCount: number = 5;
  @Input() yAxisLabel: string = 'Missed payments (months)';
  @Input() xAxisLabel: string = 'Number of policies';
  @Input() value: ChubbChartBarData[] = [];
  readonly barChartClick = output<string>();

  paths: Array<ChubbChartBarPath> = [];

  get viewBox(): string {
    return `0 0 ${this.width} ${this.height}`;
  }

  get innerWidth(): number {
    return this.width - this.paddingLeft - this.paddingRight;
  }

  get innerHeight(): number {
    return this.height - this.paddingTop - this.paddingBottom;
  }

  get maxValue(): number {
    const values = this.value.map(i => i.value);
    const rawMax = Math.max(...values);
    const step = Math.ceil(rawMax / this.yLabelCount / 2) * 2;
    return step * this.yLabelCount;
  }

  get minValue(): number {
    const values = (this.value.map(i=>i.value));
    return Math.min(...values) + 10;
  }

  get yValueScale(): number {
    return this.innerHeight / this.maxValue;
  }

  get xValueScale(): number {
    return this.innerWidth / this.maxValue;
  }

  get yAxisLabels(): Array<ChubbChartYAxisLabel> {
    const out = [];
    const step = this.maxValue / this.yLabelCount;
    const scale = this.innerHeight / this.yLabelCount / step;
    for(let i=0; i <= this.maxValue; i += step) {
      out.push({ label: Math.round(i).toString(), y: this.height - this.paddingBottom - (i * scale) + 5 });
    }
    return out;
  }

  get xAxisLabels(): Array<{ label: string; x: number }> {
    const out = [];
    const step = this.maxValue / this.yLabelCount;
    for (let i = 0; i <= this.maxValue; i += step) {
      out.push({
        label: Math.round(i).toString(),
        x: this.paddingLeft + (i / this.maxValue) * this.innerWidth
      });
    }
    return out;
  }

  get barSlotHeight(): number {
    return this.innerHeight / this.value.length;
  }

  get effectiveBarWidth(): number {
    return this.barWidth > 0 ? this.barWidth : Math.round(this.barSlotHeight * 0.4);
  }

  getHorizontalBarPath(bar: ChubbChartBarData, index: number): string {
    const barW = Math.max((bar.value / this.maxValue) * this.innerWidth, this.barRadiusTop * 2);
    const barY = this.paddingTop + this.barSlotHeight * index + (this.barSlotHeight - this.effectiveBarWidth) / 2;
    const x = this.paddingLeft;
    const y = barY;
    const w = barW;
    const h = this.effectiveBarWidth;
    const r = this.barRadiusTop;
    return `M ${x},${y} H ${x + w - r} Q ${x + w},${y} ${x + w},${y + r} V ${y + h - r} Q ${x + w},${y + h} ${x + w - r},${y + h} H ${x} Z`;
  }

  bound(v : number,min : number,max : number) : number {
    if(v<min) return min;
    if(v>max) return max;
    return v;
  }

  get grid(): Array<ChubbChartBarPath> {
    const out : Array<ChubbChartBarPath> = [];
    out.push({d:`M ${this.paddingLeft},${this.paddingTop} L ${this.paddingLeft},${this.height-this.paddingBottom}`, color: '#EEEEEE'});
    out.push({d:`M ${this.paddingLeft},${this.paddingTop} L ${this.width-this.paddingRight},${this.paddingTop}`, color: '#EEEEEE'});
    out.push({d:`M ${this.paddingLeft},${this.height-this.paddingBottom} L ${this.width-this.paddingRight},${this.height-this.paddingBottom}`, color: '#EEEEEE'});
    out.push({d:`M ${this.width-this.paddingRight},${this.paddingTop} L ${this.width-this.paddingRight},${this.height-this.paddingBottom}`, color: '#EEEEEE'});

    for (let i = 0; i <= this.yLabelCount; i++) {
      const x = this.paddingLeft + (i / this.yLabelCount) * this.innerWidth;
      out.push({d:`M ${x},${this.paddingTop} L ${x},${this.height-this.paddingBottom}`, color: '#EEEEEE'});
    }
    return out;
  }

  round(v: number): number {
    return Math.round(v);
  }

  get yAxisLabelTransform(): string {
    const x = Math.round(this.paddingLeft / 4);
    const y = Math.round(this.paddingTop + this.innerHeight / 2);
    return `translate(${x}, ${y}) rotate(-90)`;
  }

  getFilterData(label: string) {
    this.barChartClick.emit(label);
  }
}
